// Service for the Comparison Report endpoint (/cms/comparison-report/{id}) — runs
// greedy (/cms/optimize), RL (/cms/rl-optimize) and BPR (/cms/bpr-redesign) against
// one shared AS-IS baseline and returns them side by side. A read-only summary over
// the same three pipelines the other three services drive individually; no config to
// persist here beyond the optional episodes/seed/engines query params. See
// COMPARISON_REPORT.md.
const BASE_URL =
  process.env.REACT_APP_PROCESS_OPT_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  "";

function buildHeaders(extra = {}) {
  let token = null;
  try {
    const raw = sessionStorage.getItem("auth");
    if (raw) token = JSON.parse(raw)?.accessToken || null;
  } catch (_) { }
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .then((res) => { clearTimeout(timer); return res; })
    .catch((err) => {
      clearTimeout(timer);
      if (err.name === "AbortError") throw new Error(`Request timed out after ${timeoutMs / 1000}s`);
      throw err;
    });
}

// `detail` is a plain string for most errors and an object ({error, message}) for the
// 502 AS_IS_FLOW_UNAVAILABLE case (§6 of COMPARISON_REPORT.md) — normalize both into
// one Error shape.
async function throwApiError(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { }
  const detail = body?.detail;
  const message = typeof detail === "string" ? detail : (detail?.message || `HTTP ${res.status}`);
  const err = new Error(message);
  err.status = res.status;
  err.errorCode = (detail && typeof detail === "object" && detail.error) || null;
  throw err;
}

async function getJson(path, timeoutMs = 30_000) {
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, { method: "GET", headers: buildHeaders() }, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json();
}

// 15-40s typical (dominated by RL training; all three engines run concurrently
// server-side) — 60s timeout leaves headroom without hanging a Compare click forever.
//
// `includeSubprocessRedesigns` affects the RL column only, and here it IS a query
// param (unlike POST /cms/rl-optimize, which takes it in the body). It adds one
// extra training run per sub-process, ~5-15s each, so the timeout is raised to
// match — the default 60s would abort a process with three children.
export async function getComparisonReport(processId, { episodes, seed, engines, includeSubprocessRedesigns } = {}) {
  if (!processId) throw new Error("processId is required");
  const params = new URLSearchParams();
  if (episodes != null) params.set("episodes", episodes);
  if (seed != null) params.set("seed", seed);
  if (engines) params.set("engines", Array.isArray(engines) ? engines.join(",") : engines);
  if (includeSubprocessRedesigns) params.set("include_subprocess_redesigns", "true");
  const qs = params.toString();
  return getJson(
    `/cms/comparison-report/${encodeURIComponent(processId)}${qs ? `?${qs}` : ""}`,
    includeSubprocessRedesigns ? 150_000 : 60_000,
  );
}
